import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
  Box,
  Typography,
  Button,
  List,
  Divider,
  Paper,
  Checkbox,
  IconButton,
  Container,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumbs from "../../common/Breadcrumbs";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { addItem } from "../../redux/features/slices/cartSlice";
import cartDeleteIcon from "../../assets/cart-delete-icon.png";
import emptyCartImage from "../../assets/emptyCartImage.png";
import "./Cart.css";
import { CustomerEndPoints, productEndPoints } from "../../constants/endpoints";
import { api } from "../../api/api";
import {
  getCartByCustomerId,
  updateCartByCustomerIdService,
  blockInventoryService,
  unblockInventoryService,
} from "../../service/cartService";
import { cartDataMapper } from "../../utils/cartDataMapper";
import useIsMobile from "../../common/mobileVersion/useIsMobile";
//import crypto from "crypto";
import { v4 as uuidv4 } from "uuid";
import { createOrder } from "../../api/orderFinalAPI";
import Toast from "../../common/Toast";
import { getUserEmailFromLocalStorage } from "../../utils/localStorageUtils";
import { fetchProductData } from "../../api/productDataAPI";
import { mapProduct } from "../../utils/productListMapping";
import { sendCustomEvents } from "../../ga";

interface CartItem {
  id: number;
  name: string;
  price: number;
  quantity: number;
  image: string;
  unit: number;
}

const Cart: React.FC = () => {
  const [selectedSubTotalItems, setSelectedSubTotalItems] = useState<string[]>(
    []
  );
  const [productInventories, setProductInventories] = useState<
    Record<string, any>
  >({});
  const [selectedItems, setSelectedItems] = useState<number>(0);
  const [subTotal, setSubTotal] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal state
  const items = useSelector((state: RootState) => state.cart.items);
  const dispatch = useDispatch();
  const [error, setError] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false);
  const [orderPayload, setOrderPayload] = useState<any>({});
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const isMobile = useIsMobile();
  const firstRender = useRef(true);
  const [product, setProduct] = useState<any>(null);

  const navigate = useNavigate();
  const orgId = localStorage.getItem("orgId");
  useEffect(() => {
    const fetchProductInventories = async () => {
      try {
        const cartData = await getCartByCustomerId();
        const cartItems = cartData?.Products || [];
        const productIds = cartItems.map((item: any) => item.productId);
        const inventoryMap: Record<string, any> = {};
        for (const id of productIds) {
          const productData = await fetchProductData(
            id,
            productEndPoints.getProducts
          );
          const mapped = mapProduct(productData);
          inventoryMap[id] = mapped;
        }
        setProductInventories(inventoryMap);
      } catch (error) {
        console.error("Failed to fetch inventories", error);
      }
    };
    fetchProductInventories();
  }, []);

  useEffect(() => {
    //remove local storage
    localStorage.removeItem("tempPayload");

    //chnange first render to false
    if (firstRender.current) {
      firstRender.current = false;
    }

    if (items?.length) {
      const initialSelectedIds = items.map(
        (item) => `${item.id}_${item.quantity}`
      );
      setSelectedSubTotalItems(initialSelectedIds);
      setSelectedItems(initialSelectedIds.length);
    }
    const fetchCartDetails = async () => {
      try {
        const cartData = await getCartByCustomerId();
        saveItemsToStore(cartData);
      } catch (error: any) {
        console.log("error is fetching cart", error.message);
      }
    };

    fetchCartDetails();
    if (orderId) setIsModalOpen(true);

    return () => {
      const unblockInventoryOnComponentUnmount = async () => {
        try {
          const tempPayload = localStorage.getItem("tempPayload");
          if (
            !isPaymentSuccessful &&
            !firstRender.current &&
            tempPayload &&
            Object.keys(JSON.parse(tempPayload)).length
          ) {
            console.log("Payment not successful, unblocking inventory...");
            await unblockInventoryService(JSON.parse(tempPayload));
            console.log("Inventory unblocked successfully.");
          }
        } catch (error: any) {
          console.log("something went wrong while unblocking inventory", error);
        }
      };

      unblockInventoryOnComponentUnmount();
    };
  }, [isPaymentSuccessful]);

  const saveItemsToStore = (data: any) => {
    setSubTotal(+data?.subTotal);
    const items = cartDataMapper(data?.Products);
    dispatch(addItem(items));
  };

  const incrementQuantity = async (item: CartItem) => {
    try {
      const inventoryItem = productInventories[item.id];
      if (!inventoryItem) {
        setToastMessage("Inventory data not available for this product");
        setError(true);
        setShowToast(true);
        return;
      }

      const unitKey = `availableQuantity${item.quantity}L`;
      const availableQuantity = inventoryItem[unitKey];
      if (!availableQuantity || item.unit + 1 > availableQuantity) {
        setToastMessage("Cannot add more than available inventory");
        setError(true);
        setShowToast(true);
        return;
      }
      const updateCartData = {
        productId: item.id,
        unit: +item.unit > 0 ? item.unit + 1 : item.unit,
        quantity: +item.quantity,
      };
      const updatedCart = await updateCartByCustomerIdService({
        cartData: [updateCartData],
      });
      saveItemsToStore(updatedCart);
      setError(false);
      setToastMessage("Cart updated successfully");
      setShowToast(true);
    } catch (error: any) {
      setToastMessage("Failed to Increment Cart details");
      setError(true);
      setShowToast(true);
      console.log("Error in Updating", error.message);
    }
  };

  const decrementQuantity = async (item: CartItem) => {
    try {
      if (item.unit > 1) {
        const updateCartData = {
          productId: item.id,
          unit: +item.unit > 0 ? item.unit - 1 : item.unit,
          quantity: +item.quantity,
        };
        const updatedCart = await updateCartByCustomerIdService({
          cartData: [updateCartData],
        });
        saveItemsToStore(updatedCart);
        setError(false);
        setToastMessage("Cart updated successfully");
        setShowToast(true);
      } else {
        await removeItem(item);
      }
    } catch (error: any) {
      setToastMessage("Failed to Decrement Cart details");
      setError(true);
      setShowToast(true);
      console.log("Error in Updating", error.message);
    }
  };

  const removeItem = async (item: CartItem) => {
    try {
      const removeItemData = {
        productId: item.id,
        quantity: item.quantity,
        unit: 0,
      };
      const updatedCart = await updateCartByCustomerIdService({
        cartData: [removeItemData],
      });
      saveItemsToStore(updatedCart);
      if (selectedSubTotalItems.includes(`${item.id}_${item.quantity}`)) {
        setSelectedItems(selectedItems - 1);
      }
    } catch (error: any) {
      setToastMessage("Failed to Delete Cart Item");
      setError(true);
      setShowToast(true);
      console.log("Error in Updating", error.message);
    }
  };

  const emptyCart = async (removeList: string[]) => {
    // need to check if its working properly
    try {
      const removeItemData = removeList.map((item) => {
        const [id, quantity] = item.split("_");
        return { id, quantity };
      });

      const updatedCart = await updateCartByCustomerIdService({
        deletedProductsFromCart: removeItemData,
      });
      saveItemsToStore(updatedCart);
    } catch (error: any) {
      setToastMessage("Failed to Delete Cart Item");
      setError(true);
      setShowToast(true);
      console.log("Error in Updating", error.message);
    }
  };

  const getFormattedQuantity = (value: number, quantity: number): string => {
    if (!value || typeof value !== "number") {
      console.error("Invalid or empty value:", value);
      return "Invalid quantity";
    }

    const numericValue = value;
    if (isNaN(numericValue)) {
      return `${value}`;
    }

    const multiplier = Math.floor(numericValue);
    const totalLiters = multiplier * quantity;
    if (isMobile) {
      return `${totalLiters} Litres`;
    }
    if (multiplier === 1) {
      return `${numericValue} Litres`;
    }

    return `(${multiplier} x ${quantity}) ${totalLiters} Litres`;
  };

  const handleCheckboxChange = (item: CartItem) => {
    setSelectedSubTotalItems((prevSelected) => {
      if (prevSelected.includes(`${item.id}_${item.quantity}`)) {
        setSelectedItems((prevCount) => prevCount - 1);
        return prevSelected.filter(
          (itemId) => itemId !== `${item.id}_${item.quantity}`
        );
      } else {
        setSelectedItems((prevCount) => prevCount + 1);
        return [...prevSelected, `${item.id}_${item.quantity}`];
      }
    });
  };

  const selectedTotal =
    items && items.length
      ? items.reduce(
          (acc, item) =>
            selectedSubTotalItems.includes(`${item.id}_${item.quantity}`)
              ? {
                  count: acc.count + item.quantity,
                  price: acc.price + item.price * item.quantity * item.unit,
                }
              : acc,
          { count: 0, price: 0 }
        )
      : { count: 0, price: 0 };

  const tcs = 0.01;
  const grandTotal = selectedTotal.price + selectedTotal.price * tcs;

  const fetchUserData = async (emailId: string) => {
    try {
      const response = await api.get(
        `${CustomerEndPoints.getCustomerByEmail}/${emailId}`
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching user data:", error);
      throw error;
    }
  };

  const handlePlaceOrder = async () => {
    try {
      const emailId = getUserEmailFromLocalStorage() || "";
      console.log("Email retrieved from userRole key:", emailId);

      if (!emailId) {
        console.warn("No email ID found in userRole key.");
      }

      const customerData = await fetchUserData(emailId);

      const filteredProducts = items.filter((item) =>
        selectedSubTotalItems.includes(`${item.id}_${item.quantity}`)
      );

      const orderDetails = filteredProducts.map((item) => ({
        id: uuidv4(),
        orderQuantity: item.quantity,
        unit: item.unit,
        productId: item.id.toString(),
      }));

      const tempOrderPayload = {
        orderStatus: "NEW",
        paymentStatus: "PENDING",
        // totalAmountInCents: grandTotal * 100,
        totalAmountInCents: selectedTotal.price * 100,
        customerId: customerData?.customer?.id,
        taxPercentage: 10,
        discountPercentage: 5,
        billingAddress: customerData.address?.registeredAddress,
        shippingAddress: customerData.address?.licenseAddress,
        orderDetails,
      };

      localStorage.setItem("tempPayload", JSON.stringify(tempOrderPayload));

      setOrderPayload(tempOrderPayload);

      if (!(orderDetails && orderDetails.length)) {
        setToastMessage("No Items Selected For Checkout");
        setShowToast(true);
        setError(true);
        return;
      }
      await blockInventoryService({ orderDetails });

      const response: any = await createOrder(tempOrderPayload);
      const parsedResponse = JSON.parse(response.body);
      const order = parsedResponse.data.order;
      localStorage.removeItem("tempPayload");
      await emptyCart(selectedSubTotalItems);
      setIsPaymentSuccessful(true);
      setToastMessage("Order created successfully");
      setShowToast(true);
      setError(false);
      setTimeout(() => {
        navigate(`/${orgId}/customer/order-summary/${order.id}`);
      }, 2000);
    } catch (error) {
      setToastMessage("Something went wrong");
      setShowToast(true);
      setError(true);
      console.error("Error proceeding to buy:", error);
    }
  };

  return (
    <>
      <Breadcrumbs paths={["Home", "Cart"]} links={["/customer/home"]} />
      {items && items.length > 0 ? (
        <>
          <Paper elevation={3} className="cart-container" sx={{ mb: "70px" }}>
            <List>
              {items?.map((item: CartItem) => {
                const itemTotalPrice = item.price * item.quantity * item.unit;
                return (
                  <React.Fragment key={`${item.id}_${item.quantity}`}>
                    <Box className="cart-item-wrapper">
                      <Checkbox
                        size="small"
                        className="cart-checkbox"
                        checked={selectedSubTotalItems.includes(
                          `${item.id}_${item.quantity}`
                        )}
                        onChange={() => handleCheckboxChange(item)}
                      />
                      <Box className="cart-item-image">
                        <img
                          src={item.image}
                          alt="mainImage"
                          className="cart-item-img"
                        />
                      </Box>
                      <Box className="cart-item-details">
                        <Box className="cart-item-header">
                          <Typography className="cart-item-name">
                            {item.name}
                          </Typography>
                          <Typography className="cart-item-price">
                            Rs {itemTotalPrice.toLocaleString("en-IN")}
                          </Typography>
                        </Box>
                        <Typography className="cart-item-quantity">
                          Quantity:{" "}
                          {getFormattedQuantity(item.quantity, item.unit)}
                        </Typography>
                        <Box className="cart-actions">
                          <Box className="cart-quantity-btn">
                            <IconButton
                              onClick={() => {
                                sendCustomEvents(
                                  "Customer",
                                  "Clicked Decrement Quantity",
                                  `Decrement quantity for ${item.name}`
                                );
                                decrementQuantity(item);
                              }}
                              aria-label="decrease"
                              sx={{
                                width: "11px",
                                height: "14px",
                                color: "black",
                              }}
                            >
                              <RemoveIcon
                                sx={{ width: "18px", fontWeight: "bold" }}
                              />
                            </IconButton>
                            <Typography className="quantity-count">
                              {item.unit}
                            </Typography>
                            <IconButton
                              onClick={() => {
                                sendCustomEvents(
                                  "Customer",
                                  "Clicked Incremnet Quantity",
                                  `Increment quantity for ${item.name}`
                                );
                                incrementQuantity(item);
                              }}
                              aria-label="increase"
                              sx={{
                                width: "11px",
                                height: "14px",
                                color: "black",
                              }}
                            >
                              <AddIcon sx={{ width: "18px" }} />
                            </IconButton>
                          </Box>
                          <Box>
                            <IconButton
                              onClick={() => {
                                sendCustomEvents(
                                  "Customer",
                                  "Clicked Remove Item",
                                  `Remove ${item.name} from Cart`
                                );
                                removeItem(item);
                              }}
                              aria-label="delete"
                              className="cart-delete-icon"
                            >
                              <img src={cartDeleteIcon} alt="Delete" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </React.Fragment>
                );
              })}
            </List>
            <Divider />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "1rem",
              }}
            >
              <Typography
                sx={{
                  display: "flex",
                  textAlign: "right",
                  fontSize: isMobile ? "12px" : "16px",
                }}
              >
                Subtotal ({selectedItems}{" "}
                {selectedItems === 1 ? "item" : "items"}): <br />
                {/* Grand Total (Including TCS): */}
              </Typography>

              <Typography
                sx={{
                  display: "flex",
                  textAlign: "right",
                  fontWeight: 600,
                  fontSize: isMobile ? "12px" : "16px",
                }}
              >
                Rs {selectedTotal?.price.toLocaleString("en-IN")}
                <br />
                {/* Rs {grandTotal.toLocaleString("en-IN")} */}
              </Typography>
            </Box>
            <Box width={"100%"} display={"flex"} justifyContent={"right"}>
              <Typography
                sx={{
                  display: "flex",
                  // textAlign: "right",
                  fontSize: isMobile ? "12px" : "16px",
                }}
              >
                * TCS @1% applicable on Excise Invoice Only
              </Typography>
            </Box>
          </Paper>

          {/* Footer Summary */}
          <Paper
            className="cart-footer"
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100vw",
              zIndex: 1300,
              boxShadow: "4px -4px 25.9px 0px #0000001A",
            }}
          >
            <Container
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: isMobile ? "21px" : "16px 0px",
              }}
            >
              <Box className="cart-footer-summary">
                <Typography className="cart-footer-value">
                  Items Selected: {selectedItems}
                </Typography>
                <Typography className="cart-footer-value">
                  Cart Value:{" "}
                  <span className="cart-footer-bold">
                    Rs {subTotal.toLocaleString("en-IN")}
                  </span>
                </Typography>
              </Box>
              <Box>
                <Button
                  variant="contained"
                  color="primary"
                  className="cart-buy-btn"
                  aria-label="Place Order"
                  onClick={() => {
                    sendCustomEvents(
                      "Customer",
                      "Clicked Place Order Button",
                      "Place Order in Cart page"
                    );
                    handlePlaceOrder();
                  }}
                >
                  Place Order
                </Button>
              </Box>
            </Container>
          </Paper>
        </>
      ) : (
        <Box className="cart-empty">
          <img src={emptyCartImage} alt="Empty Cart" className="cart-image" />
        </Box>
      )}

      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />
    </>
  );
};

export default Cart;
